// @ts-strict-ignore
import Space from 'antd/lib/space';
import Alert from 'antd/lib/alert';
import Modal from 'antd/lib/modal';
import Radio from 'antd/lib/radio';
import Spin from 'antd/lib/spin';
import Tooltip from 'antd/lib/tooltip';
import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { createAppSettingsIntegrationsRoute } from '../../helpers/routeHelpers';
import {
    AppPlatform,
    Automation,
    AutomationType,
    BuildStatus,
    CIBuildInfo,
    CustomCIIntegrationData,
    Integration,
    IntegrationCapability,
    IntegrationType,
    ReleaseStatus,
    ReleaseStepStatus,
    ReleaseStepType,
    RunwayResponseType,
    WorkflowAndBranchData,
    WorkflowArgument,
} from '../../types';
import {
    antGrey,
    releaseStepDetailsContainerModuleStyle,
    releaseStepModuleTitleStyle,
    runwayColors,
    tagStyle,
    titleStyle,
} from '../styles/globalStyles';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Button from 'antd/lib/button';
import Text from 'antd/lib/typography/Text';
import { DeleteOutlined, PlayCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { iconAndAltForIntegrationType } from '../../helpers/integrationHelpers';
import { useDispatch, useSelector } from 'react-redux';
import { showArtifactsModuleEvenIfEmpty } from '../../helpers';
import { RootState } from '../../reducers/reducers';
import { buildStatusStringForBuild, statusColorForBuildStatus } from '../../helpers/displayHelpers';
import Link from 'antd/lib/typography/Link';
import { dayjs } from '@/extendedDayjs';
import { DatetimeFormat, convertTo24HourFormat, getLocaleDateTimeFormat } from '../../timeHelpers';
import Divider from 'antd/lib/divider';
import Tag from 'antd/lib/tag';
import Collapse from 'antd/lib/collapse';
import List from 'antd/lib/list';
import {
    StartWorkflowRequest,
    TRIGGER_RELEASE_WORKFLOW_BUILD,
    triggerReleaseWorkflowBuild,
} from '../../actions/release';
import CommitInfoModule from './CommitInfoModule';
import ArtifactsModule from './ArtifactsModule';
import CIBuildDiffSummaryModule from './CIBuildDiffSummaryModule';
import {
    CustomCITriggerIndicator,
    REDACTED_HEADER_VALUE,
    WorkflowVariablesIndicator,
} from './WorkflowVariablesIndicator';
import { AppBuildComponent } from '../BuildComponents';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { Nullable, ReleaseStepComponentProps, StepRunComponentProps } from '../../typescriptHelpers';
import * as API from '@/api';
import {
    selectAppDistributionServices,
    selectAllAppIdentifiers,
    selectAuthCredentials,
    selectAppTimeFormatSetting,
    selectAutomationById,
    selectConnectedIntegrationsForStepConfig,
    selectFirstAutomationOfType,
    selectIntegrationFromIntegrationType,
    selectIntegrationsWithCapabilityFromInstallations,
    selectRelease,
    selectStabilityIntegrations,
    selectStepConfigDataFromStepGraphs,
    selectStepRun,
} from '../../selectors/selectors';
import { globalMessage } from '../../utils/globalMessage';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { StepRunUpdatable } from '@/actions/stepRun';

interface WorkflowBuildModuleProps extends ReleaseStepComponentProps {
    releaseStepStatus: ReleaseStepStatus;
    platform: Nullable<AppPlatform>;
    workflowData?: WorkflowAndBranchData;
    sdkVersion?: string;
    buildInfo?: CIBuildInfo;
    moduleTitle?: string;
    autoUploadDsymsToStabilityMonitoringAutomationId: Nullable<string>;
    autoUploadBinaryArtifactToAppStoreAutomationId: Nullable<string>;
    autoDownloadArtifactsAutomationId: Nullable<string>;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    showDiffSummary?: boolean;
    isWorkflowTriggerAllowedForCompletedRelease?: boolean;
    allBuilds?: CIBuildInfo[];
}

const distributionSteps = [ReleaseStepType.CIDistribution, ReleaseStepType.Submission, ReleaseStepType.VCSDistribution];

interface RunWorkflowModalFormValues {
    additionalWorkflowArguments: WorkflowArgument[];
}

function additionalWorkflowArgumentsToRecord(rows: WorkflowArgument[] | undefined): Record<string, string> {
    const out: Record<string, string> = {};
    for (const row of rows ?? []) {
        const k = (row?.argumentKey ?? '').trim();
        if (!k) {
            continue;
        }
        out[k] = row?.argumentValue ?? '';
    }
    return out;
}

/** Two equal key/value columns plus a fixed action column (delete or spacer) so all rows share the same input widths. */
const runWorkflowArgRowGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) 40px',
    columnGap: 10,
    // Top-align so validation help under one field does not vertically center the sibling inputs.
    alignItems: 'start',
    marginBottom: 10,
};

const runWorkflowArgFormItemStyle: React.CSSProperties = { marginBottom: 0, width: '100%', minWidth: 0 };

enum RunInProgressChoice {
    Stop = 'stop',
    Proceed = 'proceed',
}

function inProgressRunWarning(inProgressBuilds: CIBuildInfo[], canStop: boolean): string {
    const identifiers = inProgressBuilds
        .map((build) => build.ciBuild?.buildIdentifier)
        .filter(Boolean)
        .map((identifier) => `#${identifier}`);
    const subject =
        identifiers.length === 0 ? 'A workflow is'
        : identifiers.length === 1 ? `The workflow for Build ${identifiers[0]} is`
        : `The workflows for Builds ${identifiers.join(', ')} are`;
    const outcome =
        inProgressBuilds.length > 1 ?
            'result in multiple workflows running concurrently'
        :   'result in two workflows running concurrently';

    return canStop ?
            `${subject} still running. Re-triggering without stopping will ${outcome}.`
        :   `${subject} still running. Re-triggering will ${outcome}.`;
}

function selectCIIntegrationForWorkflow(
    state: RootState,
    appId: string,
    stepConfigId: string | undefined,
    integrationType: IntegrationType | undefined,
): Integration | undefined {
    if (!integrationType) {
        return undefined;
    }

    if (!stepConfigId) {
        return selectIntegrationFromIntegrationType(state, appId, integrationType);
    }

    const ciStep = selectStepConfigDataFromStepGraphs(state, appId)[stepConfigId];
    if (ciStep) {
        const stepIntegrations = selectIntegrationsWithCapabilityFromInstallations(
            state,
            appId,
            IntegrationCapability.BuildSystem,
            ciStep.integrationInstallationIds,
        );
        const ciIntegration = stepIntegrations.find((integration) => integration.id === integrationType);
        if (ciIntegration) {
            return ciIntegration;
        }
    }

    const stepIntegrations = selectConnectedIntegrationsForStepConfig(state, appId, stepConfigId);
    return stepIntegrations.find((integration) => integration.id === integrationType);
}

interface StartWorkflowButtonDisabledReasonArgs {
    workflowData: WorkflowAndBranchData | undefined;
    triggerWorkflowAutomation: Automation | undefined;
    customCIIntegration: Integration | undefined;
    releaseStatus: ReleaseStatus;
    isWorkflowTriggerAllowedForCompletedRelease: boolean | undefined;
    orgId: string;
    appId: string;
}

function getStartWorkflowButtonDisabledReason({
    workflowData,
    triggerWorkflowAutomation,
    customCIIntegration,
    releaseStatus,
    isWorkflowTriggerAllowedForCompletedRelease,
    orgId,
    appId,
}: StartWorkflowButtonDisabledReasonArgs): React.ReactNode | undefined {
    if (!workflowData) {
        return "Workflow information isn't available. If you recently added this integration, wait a few minutes and try again. Otherwise, check your integration settings.";
    }

    if (triggerWorkflowAutomation?.unsupportedIntegrations.includes(workflowData.integrationType)) {
        return 'This integration does not support triggering workflows manually.';
    }

    const customCIData = customCIIntegration?.data as CustomCIIntegrationData | undefined;
    // Older cached integration payloads may not include hasBuildTrigger; the backend remains the guard in that case.
    if (workflowData.integrationType === IntegrationType.CustomCI && customCIData?.hasBuildTrigger === false) {
        return (
            <>
                Couldn&apos;t find a Custom CI URL. Please complete Custom CI setup in{' '}
                <RouterLink
                    to={{ pathname: createAppSettingsIntegrationsRoute(orgId, appId) }}
                    style={{ color: 'inherit', textDecoration: 'underline' }}
                >
                    Integration settings
                </RouterLink>
                .
            </>
        );
    }

    if (releaseStatus === ReleaseStatus.Completed && !isWorkflowTriggerAllowedForCompletedRelease) {
        return 'Workflows cannot be triggered for this completed release.';
    }

    return undefined;
}

const WorkflowBuildModule: React.FC<WorkflowBuildModuleProps> = (props: WorkflowBuildModuleProps) => {
    const dispatch = useDispatch();
    const isDistributionStep = distributionSteps.includes(props.releaseStepType);

    const artifactAutomations = useSelector((state: RootState) => {
        return {
            [AutomationType.AutoUploadDsymsToStabilityMonitoring]: selectAutomationById(
                state,
                props.appId,
                props.autoUploadDsymsToStabilityMonitoringAutomationId,
            ),

            [AutomationType.AutoUploadBinaryArtifactToAppStore]: selectAutomationById(
                state,
                props.appId,
                props.autoUploadBinaryArtifactToAppStoreAutomationId,
            ),
            [AutomationType.AutoDownloadArtifacts]: selectAutomationById(
                state,
                props.appId,
                props.autoDownloadArtifactsAutomationId,
            ),
        };
    });

    const ciBuild = props.buildInfo?.ciBuild;
    const associatedAppStoreBuild = props.buildInfo?.associatedAppStoreBuild;
    const workflowData = props.workflowData;

    const releaseStatus = useSelector((state: RootState) => selectRelease(state, props.releaseId).status);
    const distributionServices = useSelector((state: RootState) => selectAppDistributionServices(state, props.appId));
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, props.appId));
    const triggerWorkflowIsLoadingReleaseStep = useIsLoading(TRIGGER_RELEASE_WORKFLOW_BUILD, props.releaseId);
    const triggerWorkflowErrorReleaseStep = useError(TRIGGER_RELEASE_WORKFLOW_BUILD, props.releaseId);
    const triggerWorkflowSuccessReleaseStep = useUpdated(TRIGGER_RELEASE_WORKFLOW_BUILD, props.releaseId);

    const stepRunComponentProps = props.stepRunComponentProps;
    const stepRunId = stepRunComponentProps?.stepRunId;
    const stepRun = useSelector((state: RootState) => selectStepRun(state, stepRunId));
    const auth = useSelector((state: RootState) => selectAuthCredentials(state));

    const [
        triggerWorkflow,
        triggerWorkflowIsLoadingStepRun,
        triggerWorkflowResponseTypeStepRun,
        triggerWorkflowErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.TriggerBuildSystemWorkflow, props.stepRunComponentProps);
    const [
        startRollbackResignSequence,
        startRollbackResignSequenceIsLoading,
        startRollbackResignSequenceResponseType,
        startRollbackResignSequenceError,
    ] = useCallUpdatable(StepRunUpdatable.StartResignSequence, props.stepRunComponentProps);

    const isWorkflowRollbackBuild = props.releaseStepType === ReleaseStepType.RollbackBuild && !!stepRun;
    const triggerWorkflowIsLoading =
        isWorkflowRollbackBuild ? startRollbackResignSequenceIsLoading
        : stepRun ? triggerWorkflowIsLoadingStepRun
        : triggerWorkflowIsLoadingReleaseStep;
    const triggerWorkflowError =
        isWorkflowRollbackBuild ? startRollbackResignSequenceError
        : stepRun ? triggerWorkflowErrorStepRun
        : triggerWorkflowErrorReleaseStep;
    const triggerWorkflowSuccess =
        isWorkflowRollbackBuild ? startRollbackResignSequenceResponseType === RunwayResponseType.Success
        : stepRun ? triggerWorkflowResponseTypeStepRun === RunwayResponseType.Success
        : triggerWorkflowSuccessReleaseStep;

    const [icon, alt] = iconAndAltForIntegrationType(workflowData?.integrationType);

    const buildStatusText = buildStatusStringForBuild(ciBuild);
    const tagColor = statusColorForBuildStatus(ciBuild?.buildStatus);
    const commitLabel = ciBuild ? 'Commit' : 'Current head commit';
    const commit = ciBuild ? ciBuild.commit : (workflowData?.headCommit ?? '--');
    const commitMessage = ciBuild ? ciBuild.commitMessage : (workflowData?.headCommitMessage ?? '--');
    const commitAuthor = ciBuild ? ciBuild.commitAuthor : (workflowData?.headCommitAuthor ?? null);
    const finishedAtMoment = ciBuild?.buildFinishedAt ? dayjs(ciBuild.buildFinishedAt) : null;
    const artifacts = ciBuild ? ciBuild.artifacts : null;

    const [confirmRunWorkflowOpen, setConfirmRunWorkflowOpen] = React.useState(false);
    const [runInProgressChoice, setRunInProgressChoice] = React.useState(RunInProgressChoice.Stop);
    const [liveInProgressBuilds, setLiveInProgressBuilds] = React.useState<Nullable<CIBuildInfo[]>>(null);
    const [checkingForRunInProgress, setCheckingForRunInProgress] = React.useState(false);
    const [runWorkflowForm] = Form.useForm<RunWorkflowModalFormValues>();

    const fixedWorkflowVariableKeys = React.useMemo(
        () => new Set(Object.keys(workflowData?.workflowVariables ?? {})),
        [workflowData],
    );

    // Disable trigger button based on whether trigger automation is supported for this build system
    const triggerWorkflowAutomation = useSelector((state: RootState) =>
        selectFirstAutomationOfType(state, props.appId, AutomationType.AutoKickoffTriggerWorkflow),
    );

    const ciIntegration = useSelector((state: RootState) =>
        selectCIIntegrationForWorkflow(
            state,
            props.appId,
            props.stepRunComponentProps?.stepConfigId,
            workflowData?.integrationType,
        ),
    );

    const customCIIntegration = ciIntegration?.id === IntegrationType.CustomCI ? ciIntegration : undefined;
    const customCIData = customCIIntegration?.data as CustomCIIntegrationData | undefined;
    const isCustomCI = workflowData?.integrationType === IntegrationType.CustomCI;
    const customCITriggerBody = isCustomCI ? (customCIData?.triggerBodyTemplate ?? '') : '';
    const customCITriggerHeaderKeys = React.useMemo(
        () => (isCustomCI ? (customCIData?.triggerHeaderKeys ?? []) : []),
        [isCustomCI, customCIData],
    );
    const hasCustomCITriggerConfig = customCITriggerBody.trim() !== '' || customCITriggerHeaderKeys.length > 0;

    const startWorkflowButtonDisabledReason = getStartWorkflowButtonDisabledReason({
        workflowData,
        triggerWorkflowAutomation,
        customCIIntegration,
        releaseStatus,
        isWorkflowTriggerAllowedForCompletedRelease: props.isWorkflowTriggerAllowedForCompletedRelease,
        orgId: props.orgId,
        appId: props.appId,
    });

    const titleTextStyle: React.CSSProperties = {
        ...titleStyle,
        color: antGrey.grey7,
        fontSize: 12,
    };

    const openPanelStateForStatus = (
        status: ReleaseStepStatus,
        isDistributionStep: boolean,
        buildStatus?: BuildStatus,
    ) => {
        switch (status) {
            case ReleaseStepStatus.Inactive:
                return ['workflow'];
            default:
                if (isDistributionStep && buildStatus === BuildStatus.Success) {
                    return [];
                }

                return ['workflow'];
        }
    };

    const openConfirmRunWorkflow = () => {
        setConfirmRunWorkflowOpen(true);
    };

    React.useEffect(() => {
        let cancelled = false;
        const cleanup = () => {
            cancelled = true;
        };
        if (!confirmRunWorkflowOpen) {
            return cleanup;
        }
        runWorkflowForm.resetFields();
        runWorkflowForm.setFieldsValue({ additionalWorkflowArguments: [] });
        setRunInProgressChoice(RunInProgressChoice.Stop);
        setLiveInProgressBuilds(null);

        if (auth && stepRunComponentProps && workflowData && !isWorkflowRollbackBuild) {
            setCheckingForRunInProgress(true);
            API.getInProgressCIBuilds(stepRunComponentProps, workflowData.workflowType, auth)
                .then((response) => {
                    if (!cancelled) {
                        setLiveInProgressBuilds(response.data?.inProgressCiBuilds ?? null);
                    }
                })
                .catch(() => {
                    // Fall back to the cached view rather than blocking the trigger on a failed check.
                })
                .finally(() => {
                    if (!cancelled) {
                        setCheckingForRunInProgress(false);
                    }
                });
        }

        return cleanup;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [confirmRunWorkflowOpen]);

    const executeRunWorkflow = (additionalWorkflowVariables: Record<string, string>, stopInProgressBuilds: boolean) => {
        if (!workflowData) {
            return;
        }
        const { workflowType } = workflowData;
        const hasAdditional = Object.keys(additionalWorkflowVariables).length > 0;

        if (isWorkflowRollbackBuild) {
            startRollbackResignSequence({});
            return;
        }

        if (stepRun) {
            triggerWorkflow({
                workflowType,
                ...(hasAdditional ? { additionalWorkflowVariables } : {}),
                ...(stopInProgressBuilds ? { stopInProgressBuilds: true } : {}),
            });
            return;
        }

        const request: StartWorkflowRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
            workflowType,
            ...(hasAdditional ? { additionalWorkflowVariables } : {}),
        };

        dispatch(triggerReleaseWorkflowBuild(request));
    };

    React.useEffect(() => {
        if (triggerWorkflowError) {
            globalMessage.error(triggerWorkflowError);
        }
    }, [triggerWorkflowError]);

    React.useEffect(() => {
        if (triggerWorkflowSuccess) {
            globalMessage.success(
                isWorkflowRollbackBuild ?
                    'Your rollback rebuild sequence has been started! It may take a few seconds for the updated status to appear in Runway.'
                :   'Your workflow has been triggered! It may take a few seconds for the updated workflow status to appear in Runway.',
            );
            setConfirmRunWorkflowOpen(false);
        }
    }, [isWorkflowRollbackBuild, triggerWorkflowSuccess]);

    const justifyContent = props.moduleTitle ? 'space-between' : 'flex-end';

    const stabilityIntegrations = useSelector((state: RootState) => selectStabilityIntegrations(state, props.appId));
    const appIdentifiers = useSelector((state: RootState) => selectAllAppIdentifiers(state, props.appId));
    const version = useSelector((state: RootState) => selectRelease(state, props.releaseId)).version;

    const showArtifactsModule =
        (!!artifacts && artifacts.length > 0) || showArtifactsModuleEvenIfEmpty(artifactAutomations);

    const supportsTriggerWithWorkflowVariables =
        !isWorkflowRollbackBuild && workflowData?.supportsTriggerWithWorkflowVariables === true;

    const submitRunWorkflow = async (stopInProgressBuilds: boolean) => {
        if (!supportsTriggerWithWorkflowVariables) {
            executeRunWorkflow({}, stopInProgressBuilds);
            return;
        }
        try {
            const values = await runWorkflowForm.validateFields();
            executeRunWorkflow(
                additionalWorkflowArgumentsToRecord(values.additionalWorkflowArguments),
                stopInProgressBuilds,
            );
        } catch (e: unknown) {
            const err = e as { errorFields?: unknown[]; message?: string };
            if (err?.errorFields?.length) {
                return;
            }
            globalMessage.error(err?.message ?? 'Could not validate the form.');
        }
    };

    const cachedInProgressBuilds = React.useMemo(() => {
        if (isWorkflowRollbackBuild) {
            return [];
        }
        const builds = props.allBuilds ?? (props.buildInfo ? [props.buildInfo] : []);
        return builds.filter((build) => build.ciBuild?.buildStatus === BuildStatus.InProgress);
    }, [isWorkflowRollbackBuild, props.allBuilds, props.buildInfo]);

    const inProgressBuilds = liveInProgressBuilds ?? cachedInProgressBuilds;
    const hasRunInProgress = inProgressBuilds.length > 0;
    const canStopInProgressRun =
        hasRunInProgress && liveInProgressBuilds !== null && !!stepRun && workflowData?.supportsCancellingBuilds;
    const willStopInProgressRun = canStopInProgressRun && runInProgressChoice === RunInProgressChoice.Stop;
    const runWorkflowCtaText =
        !hasRunInProgress ? 'Run workflow'
        : willStopInProgressRun ? 'Stop current run and re-trigger'
        : 'Re-trigger without stopping current run';

    return (
        <div
            style={{
                marginBottom: 24,
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                minWidth: 0,
            }}
        >
            <Modal
                title={
                    checkingForRunInProgress ? null
                    : hasRunInProgress ?
                        'Workflow run is already in progress'
                    :   'Run workflow'
                }
                open={confirmRunWorkflowOpen}
                destroyOnClose
                onCancel={() => setConfirmRunWorkflowOpen(false)}
                footer={
                    checkingForRunInProgress ? null : (
                        <Space>
                            <Button onClick={() => setConfirmRunWorkflowOpen(false)}>Cancel</Button>
                            <Button
                                type="primary"
                                loading={triggerWorkflowIsLoading}
                                disabled={triggerWorkflowIsLoading}
                                onClick={() => submitRunWorkflow(willStopInProgressRun)}
                            >
                                {runWorkflowCtaText}
                            </Button>
                        </Space>
                    )
                }
                width={560}
            >
                {checkingForRunInProgress && (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 12,
                            padding: '32px 0',
                        }}
                    >
                        <Spin />
                        <Text type="secondary">Checking workflow run status...</Text>
                    </div>
                )}
                {!checkingForRunInProgress && hasRunInProgress && (
                    <>
                        {canStopInProgressRun && (
                            <Radio.Group
                                value={runInProgressChoice}
                                onChange={(e) => setRunInProgressChoice(e.target.value)}
                            >
                                <Space direction="vertical" size={12}>
                                    <Radio value={RunInProgressChoice.Stop}>
                                        Stop current run in progress, then re-trigger
                                    </Radio>
                                    <Radio value={RunInProgressChoice.Proceed}>
                                        Re-trigger workflow without stopping current run
                                    </Radio>
                                </Space>
                            </Radio.Group>
                        )}
                        {!willStopInProgressRun && (
                            <Alert
                                type="warning"
                                showIcon
                                style={{ marginTop: canStopInProgressRun ? 16 : 0 }}
                                message={inProgressRunWarning(inProgressBuilds, canStopInProgressRun)}
                            />
                        )}
                        <Divider />
                    </>
                )}
                {!checkingForRunInProgress && workflowData && (
                    <>
                        <Text style={{ color: antGrey.grey7, display: 'block', marginBottom: 20 }}>
                            Runway will trigger the{' '}
                            <Text style={{ color: runwayColors.black }}>{workflowData.workflowName || '—'}</Text>{' '}
                            workflow on branch <Text code>{workflowData.branch || '—'}</Text>
                            {supportsTriggerWithWorkflowVariables ?
                                <> with any workflow arguments below.</>
                            : isCustomCI && hasCustomCITriggerConfig ?
                                <> with the headers and/or request body below.</>
                            :   '.'}
                        </Text>
                        {isCustomCI && hasCustomCITriggerConfig && (
                            <div style={{ marginTop: 4 }}>
                                {customCITriggerHeaderKeys.length > 0 && (
                                    <div style={{ marginBottom: 20 }}>
                                        <Text
                                            strong
                                            style={{ display: 'block', marginBottom: 8, color: antGrey.grey8 }}
                                        >
                                            Custom headers
                                        </Text>
                                        {customCITriggerHeaderKeys.map((key) => (
                                            <div key={key} style={runWorkflowArgRowGridStyle}>
                                                <Input disabled value={key} style={{ width: '100%' }} />
                                                <Input
                                                    disabled
                                                    value={REDACTED_HEADER_VALUE}
                                                    style={{ width: '100%' }}
                                                />
                                                <span aria-hidden="true" />
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {customCITriggerBody.trim() !== '' && (
                                    <div>
                                        <Text
                                            strong
                                            style={{ display: 'block', marginBottom: 8, color: antGrey.grey8 }}
                                        >
                                            Custom request body
                                        </Text>
                                        <pre
                                            style={{
                                                margin: 0,
                                                padding: '10px 12px',
                                                background: antGrey.grey2,
                                                border: `1px solid ${antGrey.grey5}`,
                                                borderRadius: 4,
                                                fontFamily: 'monospace',
                                                fontSize: 12,
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                color: antGrey.grey8,
                                            }}
                                        >
                                            {customCITriggerBody}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        )}
                        {supportsTriggerWithWorkflowVariables && (
                            <Form
                                form={runWorkflowForm}
                                layout="vertical"
                                style={{ marginTop: 4 }}
                                initialValues={{ additionalWorkflowArguments: [] as WorkflowArgument[] }}
                            >
                                {Object.entries(workflowData.workflowVariables ?? {}).length > 0 && (
                                    <div style={{ marginBottom: 16 }}>
                                        {Object.entries(workflowData.workflowVariables ?? {}).map(([k, v]) => (
                                            <div key={k} style={runWorkflowArgRowGridStyle}>
                                                <Input disabled value={k} style={{ width: '100%' }} />
                                                <Input disabled value={v ?? ''} style={{ width: '100%' }} />
                                                <span aria-hidden="true" />
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <Form.List name="additionalWorkflowArguments">
                                    {(fields, { add, remove }) => (
                                        <div>
                                            {fields.map((field) => (
                                                <div key={field.key} style={runWorkflowArgRowGridStyle}>
                                                    <Form.Item
                                                        name={[field.name, 'argumentKey']}
                                                        rules={[
                                                            { required: true, message: 'Enter a key' },
                                                            ({ getFieldValue }) => ({
                                                                validator: async (_, value) => {
                                                                    const trimmed = (value ?? '').trim();
                                                                    if (!trimmed) {
                                                                        return;
                                                                    }
                                                                    if (fixedWorkflowVariableKeys.has(trimmed)) {
                                                                        throw new Error(
                                                                            'This key is already set in integration settings',
                                                                        );
                                                                    }
                                                                    const rows: WorkflowArgument[] =
                                                                        getFieldValue('additionalWorkflowArguments') ||
                                                                        [];
                                                                    const sameKey = rows.filter(
                                                                        (r) =>
                                                                            (r?.argumentKey ?? '').trim() === trimmed,
                                                                    );
                                                                    if (sameKey.length > 1) {
                                                                        throw new Error('Duplicate key');
                                                                    }
                                                                },
                                                            }),
                                                        ]}
                                                        style={runWorkflowArgFormItemStyle}
                                                    >
                                                        <Input placeholder="Key" />
                                                    </Form.Item>
                                                    <Form.Item
                                                        name={[field.name, 'argumentValue']}
                                                        rules={[{ required: true, message: 'Enter a value' }]}
                                                        style={runWorkflowArgFormItemStyle}
                                                    >
                                                        <Input placeholder="Value" />
                                                    </Form.Item>
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'flex-start',
                                                            paddingTop: 4,
                                                        }}
                                                    >
                                                        <Button
                                                            type="link"
                                                            danger
                                                            icon={
                                                                <DeleteOutlined
                                                                    style={{ color: runwayColors.statusRed }}
                                                                />
                                                            }
                                                            onClick={() => remove(field.name)}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                            <Button
                                                size="small"
                                                icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                                                onClick={() => add({ argumentKey: '', argumentValue: '' })}
                                            >
                                                {(
                                                    fields.length === 0 &&
                                                    Object.entries(workflowData.workflowVariables ?? {}).length == 0
                                                ) ?
                                                    'Add workflow argument'
                                                :   'Add another workflow argument'}
                                            </Button>
                                        </div>
                                    )}
                                </Form.List>
                            </Form>
                        )}
                    </>
                )}
            </Modal>
            <div style={{ display: 'flex', justifyContent, alignItems: 'baseline' }}>
                {props.moduleTitle && <Text style={{ fontSize: 20, color: antGrey.grey7 }}>{props.moduleTitle}</Text>}
                <Space size={16} style={{ marginBottom: 12 }}>
                    <Space
                        split={<Divider type="vertical" style={{ backgroundColor: antGrey.grey5 }} />}
                        align="center"
                    >
                        <Text style={{ ...releaseStepModuleTitleStyle }}>Workflow</Text>
                        <Space>
                            {workflowData &&
                                (workflowData.workflowUrl ?
                                    <Link href={workflowData.workflowUrl} target="_blank" strong>
                                        {workflowData.workflowName}
                                    </Link>
                                :   <Text style={{ color: antGrey.grey8 }}>{workflowData.workflowName}</Text>)}
                            {isCustomCI ?
                                <CustomCITriggerIndicator
                                    triggerBodyTemplate={customCITriggerBody}
                                    triggerHeaderKeys={customCITriggerHeaderKeys}
                                />
                            :   <WorkflowVariablesIndicator workflowVariables={workflowData?.workflowVariables} />}
                        </Space>
                    </Space>
                    <Tooltip title={startWorkflowButtonDisabledReason} placement="topRight">
                        <span>
                            <PermissionedActionButton
                                releaseId={props.releaseId}
                                actionType={
                                    isWorkflowRollbackBuild ?
                                        RunwayUserAction.UAStartRollbackResigningSequence
                                    :   RunwayUserAction.UATriggerCIWorkflow
                                }
                                icon={<PlayCircleOutlined style={{ color: runwayColors.info }} />}
                                onClick={openConfirmRunWorkflow}
                                disabled={!!startWorkflowButtonDisabledReason}
                                loading={triggerWorkflowIsLoading}
                            >
                                Run workflow...
                            </PermissionedActionButton>
                        </span>
                    </Tooltip>
                </Space>
            </div>
            <div
                style={{
                    ...releaseStepDetailsContainerModuleStyle,
                }}
            >
                <Collapse
                    defaultActiveKey={openPanelStateForStatus(
                        props.releaseStepStatus,
                        isDistributionStep,
                        ciBuild?.buildStatus,
                    )}
                    className="adjust-collapse-icon"
                    ghost
                >
                    <Collapse.Panel
                        className="no-padding-collapse-panel"
                        key={'workflow'}
                        header={
                            <Space align="center">
                                {icon && <img src={icon} alt={alt} />}
                                <Tag
                                    color={tagColor}
                                    style={{
                                        ...tagStyle,
                                        fontSize: props.releaseStepType == ReleaseStepType.RollbackBuild ? 11 : 14,
                                        fontWeight: props.releaseStepType == ReleaseStepType.RollbackBuild ? 600 : 400,
                                        lineHeight:
                                            props.releaseStepType == ReleaseStepType.RollbackBuild ? '22px' : null,
                                    }}
                                >
                                    {buildStatusText}
                                </Tag>
                                {!ciBuild && <Text type="secondary">No workflow runs</Text>}
                                <Space split={<Divider type="vertical"></Divider>} align="center">
                                    <Space>
                                        {ciBuild?.buildIdentifier &&
                                            (ciBuild.buildUrl ?
                                                <Link href={ciBuild.buildUrl} target="_blank" strong>
                                                    Build #{ciBuild.buildIdentifier}
                                                </Link>
                                            :   <Text style={{ color: antGrey.grey8 }} strong>
                                                    Build #{ciBuild.buildIdentifier}
                                                </Text>)}
                                        {finishedAtMoment && (
                                            <Text type="secondary">
                                                - finished{' '}
                                                {finishedAtMoment.format(
                                                    getLocaleDateTimeFormat('short', use24HourTimeFormat),
                                                )}
                                            </Text>
                                        )}
                                    </Space>
                                    {associatedAppStoreBuild && (
                                        <AppBuildComponent
                                            integrationId={associatedAppStoreBuild.integrationId}
                                            buildIdentifier={associatedAppStoreBuild.buildNumber}
                                            isProcessed={associatedAppStoreBuild.isProcessed}
                                        />
                                    )}
                                </Space>
                            </Space>
                        }
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                            <CommitInfoModule
                                commitLabel={commitLabel}
                                commit={commit}
                                commitAuthor={commitAuthor}
                                commitMessage={commitMessage}
                                branch={workflowData?.branch ?? '--'}
                            />
                            {props.platform === AppPlatform.ReactNativeOTA &&
                                ciBuild?.buildStatus === BuildStatus.Success && (
                                    <div
                                        style={{
                                            ...releaseStepDetailsContainerModuleStyle,
                                            backgroundColor: antGrey.grey2,
                                        }}
                                    >
                                        <Text style={titleTextStyle}>Details</Text>
                                        <List
                                            split={false}
                                            style={{ marginTop: 12, paddingLeft: 12, paddingRight: 12 }}
                                        >
                                            {finishedAtMoment && (
                                                <List.Item style={{ paddingTop: 0, paddingBottom: 0 }}>
                                                    <Text style={{ color: antGrey.grey8 }}>
                                                        • Uploaded at{' '}
                                                        {finishedAtMoment.format(
                                                            use24HourTimeFormat ?
                                                                convertTo24HourFormat(DatetimeFormat.TimeShortDate)
                                                            :   DatetimeFormat.TimeShortDate,
                                                        )}
                                                    </Text>
                                                </List.Item>
                                            )}
                                            {props.sdkVersion && (
                                                <List.Item style={{ paddingTop: 0, paddingBottom: 0 }}>
                                                    <Text style={{ color: antGrey.grey8 }}>
                                                        • SDK version: {props.sdkVersion}
                                                    </Text>
                                                </List.Item>
                                            )}
                                        </List>
                                    </div>
                                )}
                            {props.showDiffSummary && !!ciBuild?.aiDiffSummary && (
                                <CIBuildDiffSummaryModule summary={ciBuild.aiDiffSummary} />
                            )}
                            {!!ciBuild && showArtifactsModule && (
                                <ArtifactsModule
                                    appId={props.appId}
                                    orgId={props.orgId}
                                    releaseId={props.releaseId}
                                    version={version}
                                    artifacts={artifacts ?? []}
                                    services={distributionServices}
                                    stabilityIntegrations={stabilityIntegrations}
                                    appIdentifiers={appIdentifiers}
                                    buildStatus={ciBuild.buildStatus}
                                    canRetry={true}
                                    showHeader={true}
                                    isPublic={false}
                                    stepRunComponentProps={props.stepRunComponentProps}
                                />
                            )}
                        </div>
                    </Collapse.Panel>
                </Collapse>
            </div>
        </div>
    );
};

export default WorkflowBuildModule;
